import { ChartKey, CompileWarning, OrderEdge, VarId, varId } from '../types'

/**
 * Band-family field ordering, keyed by field NAME — never by registry position
 * (Keltner's registry order is [upper, middle, lower], unlike the others).
 * Exported so compilePattern can draw the WHOLE band when any one line is
 * referenced — the band shape is what makes these indicators recognizable.
 */
export const BAND_ORDER: Record<string, string[]> = {
  bollingerBands: ['upper', 'middle', 'lower'],
  keltnerChannel: ['upper', 'middle', 'lower'],
  donchianChannel: ['upper', 'middle', 'lower'],
  movingAverageEnvelope: ['upper', 'middle', 'lower'],
  averageTrueRangeBands: ['upper', 'lower'],
}

/** Mathematically bounded oscillators; renderMeta mirrors these pane ranges. */
const FIXED_RANGES: Record<string, [number, number]> = {
  relativeStrengthIndex: [0, 100],
  stochastic: [0, 100],
  williamsR: [-100, 0],
}

const CANDLE_SERIES = 'candle'
const OHLC_PAIRS: Array<[string, string]> = [
  ['high', 'open'],
  ['high', 'close'],
  ['open', 'low'],
  ['close', 'low'],
]

export interface InvariantOutput {
  edges: OrderEdge[]
  intervals: Record<VarId, { lo: number; hi: number }>
}

/**
 * Structural constraints injected per indicator instance on a chart: band
 * ordering (catches "BB lower crossing up through BB upper"), candle OHLC
 * consistency, and bounded-oscillator ranges.
 *
 * @param seriesByHash indicator hash (or 'candle') → set of fields present
 * @param hashToName indicator hash → indicator name (registry `value`)
 */
export const emitInvariants = (
  chartKey: ChartKey,
  seriesByHash: Map<string, Set<string>>,
  hashToName: Map<string, string>,
  axisLength: number
): InvariantOutput => {
  const edges: OrderEdge[] = []
  const intervals: Record<VarId, { lo: number; hi: number }> = {}

  seriesByHash.forEach((fields, hash) => {
    if (hash === CANDLE_SERIES) {
      for (let bar = 0; bar < axisLength; bar += 1) {
        for (const [gtField, ltField] of OHLC_PAIRS) {
          edges.push({
            gt: varId(chartKey, `candle.${gtField}`, bar),
            lt: varId(chartKey, `candle.${ltField}`, bar),
            strict: false,
            provenance: { invariant: 'candle-ohlc' },
          })
        }
      }
      return
    }

    const name = hashToName.get(hash)
    if (!name) return

    const bandOrder = BAND_ORDER[name]?.filter((field) => fields.has(field))
    if (bandOrder && bandOrder.length > 1) {
      for (let bar = 0; bar < axisLength; bar += 1) {
        for (let i = 0; i < bandOrder.length - 1; i += 1) {
          edges.push({
            gt: varId(chartKey, `${hash}.${bandOrder[i]}`, bar),
            lt: varId(chartKey, `${hash}.${bandOrder[i + 1]}`, bar),
            strict: true,
            provenance: { invariant: `${name}-band-order` },
          })
        }
      }
    }

    const range = FIXED_RANGES[name]
    if (range) {
      fields.forEach((field) => {
        for (let bar = 0; bar < axisLength; bar += 1) {
          intervals[varId(chartKey, `${hash}.${field}`, bar)] = {
            lo: range[0],
            hi: range[1],
          }
        }
      })
    }
  })

  return { edges, intervals }
}

interface GroupLike {
  id: string
  type: string
  count?: number
  conditions?: Array<{ id: string; type: string }>
  conditionGroups?: GroupLike[]
}

/**
 * Pre-solve structural checks — exactly the silent bug class the preview
 * exists to surface: a count:0 group is ALWAYS met (one empty group makes the
 * whole side fire constantly), count > children can never be met. Children are
 * direct conditions PLUS nested groups (each nested group counts as one item,
 * mirroring PDS isConditionGroupMet).
 */
export const checkGroupStructure = (groups: GroupLike[]): CompileWarning[] => {
  const warnings: CompileWarning[] = []
  const visit = (group: GroupLike): void => {
    const nested = group.conditionGroups ?? []
    const childCount = (group.conditions?.length ?? 0) + nested.length
    const count = group.count ?? childCount
    if (count === 0) {
      warnings.push({
        kind: 'alwaysMet',
        groupId: group.id,
        message:
          'This group requires 0 conditions, so it is always satisfied — the pattern can fire without any of its conditions.',
      })
    } else if (count > childCount) {
      warnings.push({
        kind: 'unsatisfiableCount',
        groupId: group.id,
        message: `This group requires ${count} conditions but only contains ${childCount} — it can never be satisfied.`,
      })
    }
    nested.forEach(visit)
  }
  groups.forEach(visit)
  return warnings
}
